import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import LazyImg from '../../components/base/LazyImg';
import { supabase } from '../../lib/supabase';
import type { Property } from '../../lib/supabase';
import { useMobileTheme, getMobilePageTheme } from '../../hooks/useMobileTheme';
import { trimToDistrict } from '../../lib/locationUtils';

const CITY_CONFIG: Record<string, {
  name: string;
  districts: string[];
  seoTitle: string;
  seoDesc: string;
  seoKeywords: string;
  heroSeq: string;
  color: string;
  bgColor: string;
  textColor: string;
  otherCities: { slug: string; name: string }[];
}> = {
  taipei: {
    name: '台北市',
    districts: ['大安區','信義區','中山區','松山區','內湖區','士林區','北投區','萬華區','中正區','南港區','文山區','大同區'],
    seoTitle: '台北市租屋｜大安 信義 中山 內湖 零仲介費屋主直租 - 藍舍AI地產',
    seoDesc: '台北市最新屋主直租房源，大安區、信義區、中山區、松山區、內湖區、士林區等各區零仲介費，直接聯繫屋主省下高額仲介費。AI智能配對，快速找到台北市理想租屋。',
    seoKeywords: '台北市租屋,台北租屋,台北市租房,大安區租屋,信義區租屋,中山區租屋,松山區租屋,內湖區租屋,士林區租屋,北投區租屋,台北零仲介費,台北屋主直租,台北免仲介費租屋',
    heroSeq: 'city-taipei-1',
    color: 'from-teal-500 to-teal-600',
    bgColor: 'bg-teal-500',
    textColor: 'text-teal-600',
    otherCities: [
      { slug: 'newtaipei', name: '新北市' },
      { slug: 'taoyuan', name: '桃園市' },
      { slug: 'taichung', name: '台中市' },
      { slug: 'tainan', name: '台南市' },
      { slug: 'kaohsiung', name: '高雄市' },
    ],
  },
  newtaipei: {
    name: '新北市',
    districts: ['板橋區','三重區','中和區','永和區','新莊區','新店區','樹林區','淡水區','汐止區','土城區','蘆洲區','五股區'],
    seoTitle: '新北市租屋｜板橋 三重 新莊 新店 零仲介費屋主直租 - 藍舍AI地產',
    seoDesc: '新北市最新屋主直租房源，板橋區、三重區、新莊區、新店區、中和區、永和區等各區零仲介費，直接聯繫屋主省下高額仲介費。AI智能配對，快速找到新北市理想租屋。',
    seoKeywords: '新北市租屋,新北租屋,板橋租屋,板橋區租屋,三重租屋,新莊租屋,新店租屋,中和租屋,永和租屋,淡水租屋,新北零仲介費,新北屋主直租,新北市免仲介費租屋',
    heroSeq: 'city-newtaipei-1',
    color: 'from-cyan-500 to-cyan-600',
    bgColor: 'bg-cyan-500',
    textColor: 'text-cyan-600',
    otherCities: [
      { slug: 'taipei', name: '台北市' },
      { slug: 'taoyuan', name: '桃園市' },
      { slug: 'taichung', name: '台中市' },
      { slug: 'tainan', name: '台南市' },
      { slug: 'kaohsiung', name: '高雄市' },
    ],
  },
  taoyuan: {
    name: '桃園市',
    districts: ['桃園區','中壢區','龜山區','蘆竹區','大園區','八德區','楊梅區','龍潭區','平鎮區','新屋區'],
    seoTitle: '桃園市租屋｜桃園 中壢 龜山 蘆竹 零仲介費屋主直租 - 藍舍AI地產',
    seoDesc: '桃園市最新屋主直租房源，桃園區、中壢區、龜山區、蘆竹區等各區零仲介費，直接聯繫屋主省下高額仲介費。AI智能配對，快速找到桃園市理想租屋。',
    seoKeywords: '桃園市租屋,桃園租屋,中壢租屋,中壢區租屋,龜山租屋,蘆竹租屋,桃園市租房,八德租屋,楊梅租屋,桃園零仲介費,桃園屋主直租,桃園免仲介費租屋',
    heroSeq: 'city-taoyuan-1',
    color: 'from-sky-500 to-sky-600',
    bgColor: 'bg-sky-500',
    textColor: 'text-sky-600',
    otherCities: [
      { slug: 'taipei', name: '台北市' },
      { slug: 'newtaipei', name: '新北市' },
      { slug: 'taichung', name: '台中市' },
      { slug: 'tainan', name: '台南市' },
      { slug: 'kaohsiung', name: '高雄市' },
    ],
  },
  taichung: {
    name: '台中市',
    districts: ['西屯區','北屯區','南屯區','豐原區','大里區','太平區','清水區','沙鹿區','烏日區','大雅區','潭子區'],
    seoTitle: '台中市租屋｜西屯 北屯 南屯 豐原 零仲介費屋主直租 - 藍舍AI地產',
    seoDesc: '台中市最新屋主直租房源，西屯區、北屯區、南屯區、豐原區、大里區等各區零仲介費，直接聯繫屋主省下高額仲介費。AI智能配對，快速找到台中市理想租屋。',
    seoKeywords: '台中市租屋,台中租屋,西屯區租屋,北屯區租屋,南屯區租屋,豐原租屋,大里租屋,台中市租房,烏日租屋,台中零仲介費,台中屋主直租,台中免仲介費租屋',
    heroSeq: 'city-taichung-1',
    color: 'from-emerald-500 to-emerald-600',
    bgColor: 'bg-emerald-500',
    textColor: 'text-emerald-600',
    otherCities: [
      { slug: 'taipei', name: '台北市' },
      { slug: 'newtaipei', name: '新北市' },
      { slug: 'taoyuan', name: '桃園市' },
      { slug: 'tainan', name: '台南市' },
      { slug: 'kaohsiung', name: '高雄市' },
    ],
  },
  tainan: {
    name: '台南市',
    districts: ['東區','永康區','安南區','仁德區','新化區','歸仁區','北區','南區','安平區','中西區','善化區'],
    seoTitle: '台南市租屋｜東區 永康 安南 仁德 零仲介費屋主直租 - 藍舍AI地產',
    seoDesc: '台南市最新屋主直租房源，東區、永康區、安南區、仁德區等各區零仲介費，直接聯繫屋主省下高額仲介費。AI智能配對，快速找到台南市理想租屋。',
    seoKeywords: '台南市租屋,台南租屋,台南東區租屋,永康區租屋,安南區租屋,仁德租屋,台南市租房,安平區租屋,台南零仲介費,台南屋主直租,台南免仲介費租屋',
    heroSeq: 'city-tainan-1',
    color: 'from-amber-500 to-amber-600',
    bgColor: 'bg-amber-500',
    textColor: 'text-amber-600',
    otherCities: [
      { slug: 'taipei', name: '台北市' },
      { slug: 'newtaipei', name: '新北市' },
      { slug: 'taoyuan', name: '桃園市' },
      { slug: 'taichung', name: '台中市' },
      { slug: 'kaohsiung', name: '高雄市' },
    ],
  },
  kaohsiung: {
    name: '高雄市',
    districts: ['苓雅區','三民區','鳳山區','左營區','前鎮區','楠梓區','鼓山區','小港區','仁武區','大寮區','林園區'],
    seoTitle: '高雄市租屋｜苓雅 三民 鳳山 左營 零仲介費屋主直租 - 藍舍AI地產',
    seoDesc: '高雄市最新屋主直租房源，苓雅區、三民區、鳳山區、左營區、前鎮區等各區零仲介費，直接聯繫屋主省下高額仲介費。AI智能配對，快速找到高雄市理想租屋。',
    seoKeywords: '高雄市租屋,高雄租屋,苓雅區租屋,三民區租屋,鳳山租屋,左營租屋,前鎮區租屋,高雄市租房,楠梓租屋,高雄零仲介費,高雄屋主直租,高雄免仲介費租屋',
    heroSeq: 'city-kaohsiung-1',
    color: 'from-rose-500 to-rose-600',
    bgColor: 'bg-rose-500',
    textColor: 'text-rose-600',
    otherCities: [
      { slug: 'taipei', name: '台北市' },
      { slug: 'newtaipei', name: '新北市' },
      { slug: 'taoyuan', name: '桃園市' },
      { slug: 'taichung', name: '台中市' },
      { slug: 'tainan', name: '台南市' },
    ],
  },
};

const PLACEHOLDER_IMAGES = [
  'https://public.readdy.ai/ai/img_res/04d86ac4e8b2bc4bebf646b2da0faef6.jpg',
  'https://readdy.ai/api/search-image?query=cozy%20living%20room%20apartment%20warm%20lighting%20comfortable%20furniture%20clean%20simple%20interior%20professional%20real%20estate%20photo%20neutral%20background&width=600&height=400&seq=cp2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=contemporary%20apartment%20bedroom%20natural%20light%20clean%20design%20simple%20furniture%20white%20walls%20professional%20photography&width=600&height=400&seq=cp3&orientation=landscape',
];

export default function CityPage() {
  const { city } = useParams<{ city: string }>();
  const config = city ? CITY_CONFIG[city] : null;
  const { theme } = useMobileTheme();
  const pt = getMobilePageTheme(theme);

  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);

  useEffect(() => {
    if (!config) return;
    const fetchProperties = async () => {
      setLoading(true);
      let query = supabase.from('properties').select('*').eq('status', 'active').ilike('location', `${config.name}%`).order('created_at', { ascending: false }).limit(24);
      if (selectedDistrict) {
        query = query.ilike('location', `%${selectedDistrict}%`);
      }
      const { data } = await query;
      setProperties(data || []);
      setLoading(false);
    };
    fetchProperties();
  }, [config, selectedDistrict]);

  if (!config) return <Navigate to="/regions" replace />;

  return (
    <div className={`min-h-screen ${pt.pageBg}`}>
      <SeoHead
        title={config.seoTitle}
        description={config.seoDesc}
        keywords={config.seoKeywords}
        canonical={`/regions/${city}`}
        lastModified="2026-03-18"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SearchResultsPage",
            "name": `${config.name}租屋 - 藍舍AI地產`,
            "url": `https://ailanshe.com/regions/${city}`,
            "description": config.seoDesc,
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首頁", "item": "https://ailanshe.com/" },
                { "@type": "ListItem", "position": 2, "name": "六都房源", "item": "https://ailanshe.com/regions" },
                { "@type": "ListItem", "position": 3, "name": `${config.name}租屋`, "item": `https://ailanshe.com/regions/${city}` }
              ]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `${config.name}哪裡找零仲介費租屋？`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `藍舍AI地產提供${config.name}${config.districts.slice(0, 4).join('、')}等各區零仲介費屋主直租房源，完全免仲介費，直接與屋主聯繫看房。`
                }
              },
              {
                "@type": "Question",
                "name": `${config.name}租屋行情大概多少？`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `${config.name}租屋依地段、房型差異很大。可在藍舍AI地產平台搜尋${config.name}最新屋主直租房源，比較各區租金行情，零仲介費省下高額中介費用。`
                }
              }
            ]
          }
        ]}
      />
      <Navbar />

      {/* Hero */}
      <div className="relative h-52 md:h-72 flex items-end overflow-hidden">
        <img
          src={`https://readdy.ai/api/search-image?query=aerial%20cityscape%20view%20of%20$%7Bconfig.name%7D%20Taiwan%20urban%20neighborhood%20with%20residential%20buildings%20streets%20and%20greenery%2C%20clear%20sky%20warm%20natural%20light%2C%20modern%20city%20photography&width=1920&height=600&seq=${config.heroSeq}&orientation=landscape`}
          alt={`${config.name}租屋`}
          className="hidden md:block absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* 手機版純色背景 */}
        <div className={`md:hidden absolute inset-0 ${config.bgColor}`} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/35 to-black/65"></div>
        <div className="relative z-10 w-full pb-8">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-white/60 text-xs mb-3">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">首頁</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/regions" className="hover:text-white transition-colors cursor-pointer">六都房源</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-white font-semibold">{config.name}</span>
            </nav>
            <h1 className="text-3xl md:text-5xl font-black text-white drop-shadow-lg mb-1.5">
              {config.name}租屋
            </h1>
            <p className="text-sm md:text-base text-white/85 drop-shadow">
              零仲介費・直接租房・AI智能配對
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">

        {/* 行政區快速篩選 */}
        <div className="bg-white rounded-2xl p-4 md:p-6 mb-6 md:mb-8">
          <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <i className={`ri-map-pin-line ${pt.iconColor}`}></i>
            依行政區篩選
            <span className="text-xs font-normal text-gray-400">（不選則顯示全市）</span>
          </h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedDistrict(null)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${!selectedDistrict ? config.bgColor + ' text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              全部
            </button>
            {config.districts.map(d => (
              <button
                key={d}
                onClick={() => setSelectedDistrict(selectedDistrict === d ? null : d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${selectedDistrict === d ? config.bgColor + ' text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* 房源數量 */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <p className="text-sm text-gray-500">
            {loading ? '載入中...' : (
              <>共 <strong className={pt.accentText}>{properties.length}</strong> 筆{config.name}{selectedDistrict ? selectedDistrict : ''}零仲介費房源</>
            )}
          </p>
          <Link to="/regions" className={`text-xs ${pt.sectionAccent} hover:opacity-80 flex items-center gap-1 cursor-pointer whitespace-nowrap`}>
            <i className="ri-equalizer-line text-sm"></i>
            進階篩選
          </Link>
        </div>

        {/* 房源列表 */}
        {loading ? (
          <div className="flex items-center justify-center py-20 bg-white rounded-2xl">
            <i className={`ri-loader-4-line text-4xl ${pt.loadingColor} animate-spin`}></i>
          </div>
        ) : properties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl mb-10">
            <div className={`w-16 h-16 flex items-center justify-center ${pt.emptyIconBg} rounded-full mb-4`}>
              <i className={`ri-home-4-line text-3xl ${pt.emptyIconColor}`}></i>
            </div>
            <p className="font-bold text-gray-600 mb-2">目前尚無{config.name}房源</p>
            <p className="text-sm text-gray-400 mb-6">成為第一個在{config.name}發布房源的屋主！</p>
            <Link to="/publish" className={`px-6 py-2.5 ${pt.primaryBtn} rounded-full font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap`}>
              立即發布房源
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 mb-10">
            {properties.map(property => {
              const img = property.photos?.[0] ?? PLACEHOLDER_IMAGES[property.id.charCodeAt(0) % 3];
              const pos = property.photo_positions?.[0] ?? '50% 50%';
              const zoom = property.photo_zooms?.[0] ?? 1;
              return (
                <Link
                  key={property.id}
                  to={`/property/${property.id}`}
                  className="bg-white rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  {/* 圖片容器：加 bg-gray-100 作為 skeleton 底色 */}
                  <div className="relative h-44 overflow-hidden bg-gray-100">
                    <LazyImg
                      src={img}
                      alt={property.title}
                      containerClassName="absolute inset-0"
                      imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      imgStyle={{ objectPosition: pos, transform: `scale(${zoom})`, transformOrigin: pos }}
                    />
                    <span className="absolute top-2.5 left-2.5 px-2.5 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">零仲介費</span>
                    <span className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-white/90 text-teal-600 text-xs font-bold rounded-full flex items-center gap-1">
                      <i className="ri-shield-check-line text-xs"></i>零仲介費
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-1 mb-1.5">{property.title}</h3>
                    <p className="text-xs text-gray-400 flex items-center gap-1 mb-2.5 truncate">
                      <i className="ri-map-pin-line text-teal-400 flex-shrink-0"></i>
                      <span className="truncate">{trimToDistrict(property.location)}</span>
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
                      <span className="px-2 py-0.5 bg-gray-50 rounded-md">{property.area}坪</span>
                      <span className="px-2 py-0.5 bg-gray-50 rounded-md">
                        {['店面', '廠房', '辦公室'].includes(property.property_type || '')
                          ? <><i className="ri-store-2-line mr-0.5"></i>{property.property_type}・{property.area}坪・{property.bathrooms}衛</>
                          : `${property.rooms}房${property.bathrooms}衛`
                        }
                      </span>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-lg font-black text-red-500">NT$ {property.price.toLocaleString()}</span>
                        <span className="text-xs text-gray-400">/月</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-300">
                        <i className="ri-eye-line"></i>
                        <span>{property.view_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* SEO 內容：行政區關鍵字 */}
        <div className="bg-white rounded-2xl p-5 md:p-7 mb-8">
          <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
            <i className="ri-map-2-line text-teal-500"></i>
            {config.name}各行政區租屋
          </h2>
          <div className="flex flex-wrap gap-2">
            {config.districts.map(d => (
              <button
                key={d}
                onClick={() => setSelectedDistrict(d)}
                className="px-3 py-1.5 bg-stone-50 hover:bg-teal-50 hover:text-teal-700 text-gray-600 text-xs rounded-lg border border-stone-200 hover:border-teal-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {d}零仲介費租屋
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4 leading-relaxed">
            藍舍AI地產提供{config.name}全區屋主直租房源，包含{config.districts.slice(0, 6).join('、')}等各行政區，全部零仲介費，直接與屋主聯繫，省下高額仲介費用。
          </p>
        </div>

        {/* 其他城市 */}
        <div className="bg-white rounded-2xl p-5 md:p-7 mb-8">
          <h2 className="text-base font-bold text-gray-800 mb-4">其他城市租屋</h2>
          <div className="flex flex-wrap gap-2.5">
            {config.otherCities.map(c => (
              <Link
                key={c.slug}
                to={`/regions/${c.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 bg-stone-50 hover:bg-teal-50 border border-stone-200 hover:border-teal-300 text-gray-700 hover:text-teal-700 text-sm font-medium rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-line text-xs text-teal-400"></i>
                {c.name}租屋
              </Link>
            ))}
            <Link
              to="/regions"
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-50 border border-teal-200 text-teal-700 text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap hover:bg-teal-100"
            >
              <i className="ri-search-line text-xs"></i>
              進階篩選找房
            </Link>
          </div>
        </div>

        {/* AI 媒合 CTA */}
        <div className={`bg-gradient-to-r ${pt.ctaGradient} rounded-2xl p-6 md:p-8 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-5`}>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-sparkling-2-fill text-amber-300 text-lg"></i>
              <h2 className="font-black text-lg">找不到理想的{config.name}房源？</h2>
            </div>
            <p className="text-white/80 text-sm">讓 AI 幫你等，有符合需求的房源上架立即通知</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to="/ai-match"
              className="flex items-center gap-2 px-6 py-3 bg-white text-teal-600 rounded-full font-bold text-sm hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-robot-2-line"></i>
              提交 AI 媒合需求
            </Link>
            <Link
              to="/publish"
              className="flex items-center gap-2 px-5 py-3 bg-white/20 text-white rounded-full font-medium text-sm hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap border border-white/30"
            >
              我是屋主
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
